<template>
  <div
    v-if="visible"
    class="loading-container"
    :class="[
      {
        'loading-fullscreen': fullscreen,
        'loading-absolute': !fullscreen,
        'loading-with-backdrop': backdrop
      }
    ]"
    :style="containerStyle"
  >
    <!-- 背景遮罩 -->
    <div
      v-if="backdrop"
      class="loading-backdrop"
      :style="{ backgroundColor: backdropColor }"
    ></div>

    <!-- 加载内容 -->
    <div class="loading-content" :style="contentStyle">
      <!-- 加载图标 -->
      <div class="loading-spinner">
        <!-- 默认旋转图标 -->
        <div v-if="type === 'spinner'" class="spinner-default">
          <div class="spinner-ring"></div>
        </div>

        <!-- 点状加载 -->
        <div v-else-if="type === 'dots'" class="spinner-dots">
          <div v-for="i in 3" :key="i" class="dot"></div>
        </div>

        <!-- 波浪加载 -->
        <div v-else-if="type === 'wave'" class="spinner-wave">
          <div v-for="i in 5" :key="i" class="wave-bar"></div>
        </div>

        <!-- 脉冲加载 -->
        <div v-else-if="type === 'pulse'" class="spinner-pulse">
          <div class="pulse-circle"></div>
        </div>

        <!-- 自定义图标 -->
        <div v-else-if="type === 'custom'" class="spinner-custom">
          <slot name="icon">
            <ElIcon class="loading-icon" :size="iconSize">
              <Loading />
            </ElIcon>
          </slot>
        </div>

        <!-- Element Plus 加载 -->
        <ElIcon v-else class="loading-icon" :size="iconSize">
          <Loading />
        </ElIcon>
      </div>

      <!-- 加载文本 -->
      <div v-if="text" class="loading-text" :style="textStyle">
        {{ text }}
      </div>

      <!-- 进度条 -->
      <div v-if="showProgress" class="loading-progress">
        <ElProgress
          :percentage="progress"
          :show-text="showProgressText"
          :stroke-width="progressStrokeWidth"
          :color="progressColor"
        />
      </div>

      <!-- 取消按钮 -->
      <div v-if="cancelable" class="loading-actions">
        <ElButton
          size="small"
          @click="handleCancel"
        >
          {{ cancelText }}
        </ElButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Loading 加载组件
 * 支持多种加载样式和配置选项
 * @author AI Assistant
 * @date 2024-01-01
 */

import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { Loading } from '@element-plus/icons-vue'
import type { ComponentSize } from 'element-plus'

// 接口定义
interface Props {
  /** 是否显示加载 */
  visible?: boolean
  /** 加载文本 */
  text?: string
  /** 加载类型 */
  type?: 'default' | 'spinner' | 'dots' | 'wave' | 'pulse' | 'custom'
  /** 是否全屏 */
  fullscreen?: boolean
  /** 是否显示背景遮罩 */
  backdrop?: boolean
  /** 背景颜色 */
  backdropColor?: string
  /** 图标大小 */
  iconSize?: number
  /** 文本颜色 */
  textColor?: string
  /** 文本大小 */
  textSize?: string
  /** 主题色 */
  color?: string
  /** 组件大小 */
  size?: ComponentSize
  /** 是否可取消 */
  cancelable?: boolean
  /** 取消按钮文本 */
  cancelText?: string
  /** 是否显示进度条 */
  showProgress?: boolean
  /** 进度值 */
  progress?: number
  /** 是否显示进度文本 */
  showProgressText?: boolean
  /** 进度条粗细 */
  progressStrokeWidth?: number
  /** 进度条颜色 */
  progressColor?: string
  /** 延迟显示时间(ms) */
  delay?: number
  /** 最小显示时间(ms) */
  minDuration?: number
  /** z-index */
  zIndex?: number
}

interface Emits {
  /** 取消事件 */
  'cancel': []
  /** 显示状态变化 */
  'update:visible': [visible: boolean]
}

// 定义 props
const props = withDefaults(defineProps<Props>(), {
  visible: false,
  text: '',
  type: 'default',
  fullscreen: false,
  backdrop: true,
  backdropColor: 'rgba(255, 255, 255, 0.8)',
  iconSize: 24,
  textColor: '#606266',
  textSize: '14px',
  color: '#409eff',
  size: 'default',
  cancelable: false,
  cancelText: '取消',
  showProgress: false,
  progress: 0,
  showProgressText: true,
  progressStrokeWidth: 6,
  progressColor: '#409eff',
  delay: 0,
  minDuration: 0,
  zIndex: 2000
})

// 定义 emits
const emit = defineEmits<Emits>()

// 内部状态
const actualVisible = ref(false)
const showTime = ref(0)
let delayTimer: NodeJS.Timeout | null = null
let minDurationTimer: NodeJS.Timeout | null = null

// 计算属性
const containerStyle = computed(() => ({
  zIndex: props.zIndex,
  '--loading-color': props.color
}))

const contentStyle = computed(() => {
  const sizeMap = {
    large: '120px',
    default: '100px',
    small: '80px'
  }

  return {
    minWidth: sizeMap[props.size],
    minHeight: sizeMap[props.size]
  }
})

const textStyle = computed(() => ({
  color: props.textColor,
  fontSize: props.textSize
}))

// 监听 visible 变化
watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      showLoading()
    } else {
      hideLoading()
    }
  },
  { immediate: true }
)

/**
 * 显示加载
 */
const showLoading = (): void => {
  if (props.delay > 0) {
    delayTimer = setTimeout(() => {
      actualVisible.value = true
      showTime.value = Date.now()
    }, props.delay)
  } else {
    actualVisible.value = true
    showTime.value = Date.now()
  }
}

/**
 * 隐藏加载
 */
const hideLoading = (): void => {
  if (delayTimer) {
    clearTimeout(delayTimer)
    delayTimer = null
    return
  }

  const elapsed = Date.now() - showTime.value
  const remaining = props.minDuration - elapsed

  if (remaining > 0) {
    minDurationTimer = setTimeout(() => {
      actualVisible.value = false
    }, remaining)
  } else {
    actualVisible.value = false
  }
}

/**
 * 取消处理
 */
const handleCancel = (): void => {
  emit('cancel')
  emit('update:visible', false)
}

/**
 * 阻止滚动
 */
const preventScroll = (e: Event): void => {
  e.preventDefault()
}

// 生命周期
onMounted(() => {
  if (props.fullscreen && actualVisible.value) {
    document.body.style.overflow = 'hidden'
    document.addEventListener('wheel', preventScroll, { passive: false })
    document.addEventListener('touchmove', preventScroll, { passive: false })
  }
})

onUnmounted(() => {
  if (delayTimer) {
    clearTimeout(delayTimer)
  }
  if (minDurationTimer) {
    clearTimeout(minDurationTimer)
  }

  if (props.fullscreen) {
    document.body.style.overflow = ''
    document.removeEventListener('wheel', preventScroll)
    document.removeEventListener('touchmove', preventScroll)
  }
})

// 监听全屏状态变化
watch(
  [() => props.fullscreen, actualVisible],
  ([fullscreen, visible]) => {
    if (fullscreen && visible) {
      document.body.style.overflow = 'hidden'
      document.addEventListener('wheel', preventScroll, { passive: false })
      document.addEventListener('touchmove', preventScroll, { passive: false })
    } else {
      document.body.style.overflow = ''
      document.removeEventListener('wheel', preventScroll)
      document.removeEventListener('touchmove', preventScroll)
    }
  }
)

// 暴露方法
defineExpose({
  show: () => emit('update:visible', true),
  hide: () => emit('update:visible', false),
  toggle: () => emit('update:visible', !props.visible)
})
</script>

<style scoped>
.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.loading-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
}

.loading-absolute {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.loading-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(8px);
  background: rgba(0, 0, 0, 0.1);
}

.loading-content {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.loading-spinner {
  margin-bottom: 16px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.loading-icon {
  color: var(--loading-color, #409eff);
  animation: loading-rotate 2s linear infinite;
}

.loading-text {
  margin-top: 8px;
  font-weight: 600;
  line-height: 1.5;
  text-align: center;
  color: var(--el-text-color-primary);
  letter-spacing: 0.3px;
}

.loading-progress {
  margin-top: 16px;
  width: 200px;
}

.loading-actions {
  margin-top: 16px;
}

/* 旋转动画 */
@keyframes loading-rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 默认旋转器 */
.spinner-default {
  position: relative;
  width: 32px;
  height: 32px;
}

.spinner-ring {
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-top: 3px solid var(--loading-color, #409eff);
  border-radius: 50%;
  animation: loading-rotate 1s linear infinite;
}

/* 点状加载器 */
.spinner-dots {
  display: flex;
  gap: 4px;
}

.spinner-dots .dot {
  width: 8px;
  height: 8px;
  background: var(--loading-color, #409eff);
  border-radius: 50%;
  animation: dots-bounce 1.4s ease-in-out infinite both;
}

.spinner-dots .dot:nth-child(1) {
  animation-delay: -0.32s;
}

.spinner-dots .dot:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes dots-bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

/* 波浪加载器 */
.spinner-wave {
  display: flex;
  gap: 2px;
  align-items: center;
  height: 32px;
}

.wave-bar {
  width: 4px;
  height: 100%;
  background: var(--loading-color, #409eff);
  animation: wave-stretch 1.2s ease-in-out infinite;
}

.wave-bar:nth-child(1) {
  animation-delay: -1.2s;
}

.wave-bar:nth-child(2) {
  animation-delay: -1.1s;
}

.wave-bar:nth-child(3) {
  animation-delay: -1.0s;
}

.wave-bar:nth-child(4) {
  animation-delay: -0.9s;
}

.wave-bar:nth-child(5) {
  animation-delay: -0.8s;
}

@keyframes wave-stretch {
  0%, 40%, 100% {
    transform: scaleY(0.4);
  }
  20% {
    transform: scaleY(1);
  }
}

/* 脉冲加载器 */
.spinner-pulse {
  position: relative;
  width: 32px;
  height: 32px;
}

.pulse-circle {
  width: 100%;
  height: 100%;
  background: var(--loading-color, #409eff);
  border-radius: 50%;
  animation: pulse-scale 1s ease-in-out infinite;
}

@keyframes pulse-scale {
  0% {
    transform: scale(0);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0;
  }
}

/* 自定义加载器 */
.spinner-custom {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .loading-content {
    padding: 16px;
    min-width: 80px;
    min-height: 80px;
  }

  .loading-progress {
    width: 150px;
  }
}

/* 暗色主题适配 */
@media (prefers-color-scheme: dark) {
  .loading-content {
    background: rgba(30, 30, 30, 0.95);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }

  .loading-backdrop {
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(12px);
  }

  .loading-text {
    color: rgba(255, 255, 255, 0.9);
  }
}

/* Vue深色模式类适配 */
.dark .loading-content {
  background: rgba(30, 30, 30, 0.95);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.dark .loading-backdrop {
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(12px);
}

.dark .loading-text {
  color: rgba(255, 255, 255, 0.9);
}

/* 动画性能优化 */
.loading-container * {
  will-change: transform, opacity;
}

/* 无障碍支持 */
.loading-container[aria-hidden="true"] {
  pointer-events: none;
}

/* 打印样式 */
@media print {
  .loading-container {
    display: none !important;
  }
}
</style>
